import { computeVariance } from "@/lib/costVariance";
import { formatCZK } from "@/components/Shared";
import { TrendingDown, TrendingUp, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Inline badge porovnávající skutečnou výrobní cenu s plánovanou.
 * Pokud výrobek nemá plán nastavený, vrátí "—".
 *
 * Variants:
 *  - "inline" (default): kompaktní pro řádky tabulek
 *  - "block": větší pro karty / dialogy
 */
export default function CostVarianceBadge({ actualUnitCost, product, variant = "inline", showPlanned = false }) {
  const v = computeVariance(actualUnitCost, product);
  if (!v) {
    return (
      <span className="text-xs text-[#A8A29E]" title="Plánovaná cena není nastavena">
        —
      </span>
    );
  }

  const isOver = v.diff > 0;
  const Arrow = isOver ? TrendingUp : TrendingDown;
  const Icon = v.overTolerance ? AlertTriangle : CheckCircle2;

  // Color tokens
  const colors = v.overTolerance
    ? { bg: "#9E47471A", fg: "#9E4747", border: "#9E47474D" } // red
    : { bg: "#376A4D1A", fg: "#376A4D", border: "#376A4D4D" }; // green

  const pctText = `${v.pct > 0 ? "+" : ""}${v.pct.toFixed(1)} %`;
  const tipLines = [
    `Plán: ${formatCZK(v.planned)}/ks`,
    `Skutečnost: ${formatCZK(v.actual)}/ks`,
    `Rozdíl: ${v.diff > 0 ? "+" : ""}${formatCZK(Math.abs(v.diff))}/ks (${pctText})`,
    `Tolerance: ±${v.tolerance.toFixed(0)} %`,
    v.overTolerance ? "MIMO TOLERANCI" : "V toleranci",
  ].join("\n");

  if (variant === "block") {
    return (
      <div
        className="inline-flex flex-col items-start gap-0.5 px-2.5 py-1.5 rounded-md border text-xs"
        style={{ background: colors.bg, color: colors.fg, borderColor: colors.border }}
        title={tipLines}
        data-testid="cost-variance-badge"
        data-level={v.level}
      >
        <div className="flex items-center gap-1 font-medium">
          <Icon className="w-3 h-3" />
          <span>{pctText}</span>
        </div>
        {showPlanned && (
          <div className="text-[10px] opacity-80">plán {formatCZK(v.planned)}/ks</div>
        )}
      </div>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border whitespace-nowrap"
      style={{ background: colors.bg, color: colors.fg, borderColor: colors.border }}
      title={tipLines}
      data-testid="cost-variance-badge"
      data-level={v.level}
    >
      <Arrow className="w-3 h-3" />
      {pctText}
      {v.overTolerance && <AlertTriangle className="w-3 h-3" />}
    </span>
  );
}
